import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  StatusBadge,
  CenteredContainer,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  handlePlainLinkClick,
} from '@bastion/ui';
import {
  applyFilters,
  computeOptionCounts,
  EMPTY_FILTER_STATE,
  ListViewToolbar,
  type FilterConfig,
  type FilterState,
} from '@bastion/list-view';
import {
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Link as LinkIcon,
  RefreshCw,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { useDatasets } from '@bastion/data';
import { useWorkflowRuns, useWorkflowRunTags, useUpdateWorkflowRun } from '../../hooks/useWorkflows';
import type { WorkflowRun, WorkflowRunStatus, WorkflowRunTagCount } from '../../types';
import {
  WORKFLOW_RUN_STATUS_LABELS,
  WORKFLOW_RUN_STATUS_COLORS,
  ARTIFACT_TYPE_LABELS,
  formatDuration,
  type ArtifactType,
} from '../../types';
import { groupRunsByWorkflow, type WorkflowGroup } from './groupWorkflows';
import { useWorkflowsListStore } from './workflowsListStore';
import { createdTimeRangeFilterConfig, matchesCreatedRange, resolveCreatedRange } from '../../utils/timeRangeFilters';
import css from './WorkflowsList.module.css';

const MAX_VISIBLE_RUNS = 10;

const WORKFLOW_STATUS_FILTER_ORDER: WorkflowRunStatus[] = [
  'draft',
  'waiting_for_gpu',
  'running',
  'completed',
  'failed',
];

// ── Columns ────────────────────────────────────────────────────────────────
//
// One column = one cell of data per sub-row, plus its header label. The
// header is hoisted to the top of the accordion (rendered once), so we
// drive both the header row and each sub-row off the same definition so
// they stay in lockstep when the user toggles visibility.

export type WorkflowColumnId =
  | 'name'
  | 'status'
  | 'artifacts'
  | 'tags'
  | 'duration'
  | 'created';

export interface WorkflowColumnConfig {
  readonly id: WorkflowColumnId;
  readonly label: string;
  readonly width: string;
  /** Columns that can never be hidden (e.g. the run name). */
  readonly alwaysOn?: boolean;
}

export const WORKFLOW_COLUMNS: readonly WorkflowColumnConfig[] = [
  { id: 'name', label: 'Name', width: 'minmax(0, 1fr)', alwaysOn: true },
  { id: 'status', label: 'Status', width: '140px' },
  { id: 'artifacts', label: 'Artifacts', width: '120px' },
  { id: 'tags', label: 'Tags', width: '160px' },
  { id: 'duration', label: 'Duration', width: '90px' },
  { id: 'created', label: 'Created', width: '100px' },
];

function visibleColumnsFor(hidden: Set<string>): WorkflowColumnConfig[] {
  return WORKFLOW_COLUMNS.filter((c) => c.alwaysOn || !hidden.has(c.id));
}

function gridTemplateFor(columns: readonly WorkflowColumnConfig[]): string {
  return columns.map((c) => c.width).join(' ');
}

/**
 * Columns that a narrow table on a phone or a touch screen takes out of the
 * grid. Artifacts does not show, and Tags moves to its own line.
 */
const COMPACT_OMITTED_COLUMNS: ReadonlySet<WorkflowColumnId> = new Set(['artifacts', 'tags']);

/** The class of each header cell that a narrow table hides. */
const HEADER_CELL_CLASS: Partial<Record<WorkflowColumnId, string>> = {
  artifacts: css.headerCellArtifacts,
  tags: css.headerCellTags,
};

export const WORKFLOW_FILTER_IDS = [
  'status',
  'tag',
  'workflow_name',
  'artifact_type',
  'dataset',
  'created',
] as const;
export type WorkflowFilterId = (typeof WORKFLOW_FILTER_IDS)[number];

export const WORKFLOW_FILTER_URL_KINDS = {
  created: 'time-range',
} as const;

/**
 * Status-filter shortcuts used by the preset tabs. Each tab IS the equivalent
 * of a particular `status` chip value-set — the tabs are pure UI sugar over
 * `filterState`, so the chip / URL is the only source of truth.
 */
export type WorkflowTabId = 'all' | 'active' | 'completed';

const TABS: { id: WorkflowTabId; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'completed', label: 'Completed' },
];

const ACTIVE_STATUSES: WorkflowRunStatus[] = ['draft', 'waiting_for_gpu', 'running'];
const COMPLETED_STATUSES: WorkflowRunStatus[] = ['completed'];

function filterSignature(filters: readonly FilterState['filters'][number][]): string {
  return filters
    .map((filter) => {
      const values = [...filter.values].sort().join(',');
      return `${filter.attributeId}:${filter.operator}:${values}`;
    })
    .sort()
    .join('|');
}

function valuesEqual(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((value, idx) => value === sortedB[idx]);
}

/**
 * Map the current `filterState` to whichever tab matches. Returns `null`
 * when the status chip is set to something custom (mixed statuses); the
 * tab strip then shows nothing highlighted to signal the chip wins.
 */
export function deriveWorkflowTab(state: FilterState): WorkflowTabId | null {
  const status = state.filters.find((f) => f.attributeId === 'status');
  if (!status || status.values.length === 0) return 'all';
  if (status.operator !== 'is') return null;
  if (valuesEqual(status.values, ACTIVE_STATUSES)) return 'active';
  if (valuesEqual(status.values, COMPLETED_STATUSES)) return 'completed';
  return null;
}

/**
 * Compose a new filter state by replacing the `status` chip with whatever
 * the picked tab dictates. Other chips (tag, workflow_name, …) are left
 * untouched.
 */
export function applyWorkflowTabToFilters(
  state: FilterState,
  tab: WorkflowTabId,
): FilterState {
  const other = state.filters.filter((f) => f.attributeId !== 'status');
  if (tab === 'all') {
    return { filters: other };
  }
  const values = tab === 'active' ? ACTIVE_STATUSES : COMPLETED_STATUSES;
  return {
    filters: [...other, { attributeId: 'status', operator: 'is', values }],
  };
}

interface PresetTabsProps {
  filterState: FilterState;
  onFilterStateChange: (next: FilterState) => void;
}

/**
 * Presets behave as quick-apply buttons: they're only visible when no chips
 * are active. As soon as the user picks Active/Completed (which adds a status
 * chip) or applies any other chip filter, the preset strip steps out of the
 * way — the chips themselves are the canonical control from that point.
 * X-ing every chip brings the presets back, defaulted to All.
 */
function PresetTabs({ filterState, onFilterStateChange }: PresetTabsProps) {
  if (filterState.filters.length > 0) {
    return null;
  }
  return (
    <div className={css.presetTabs} aria-label="Workflow run presets">
      {TABS.map((p) => (
        <button
          key={p.id}
          type="button"
          className={`${css.presetTab} ${p.id === 'all' ? css.presetTabActive : ''}`}
          onClick={() => onFilterStateChange(applyWorkflowTabToFilters(filterState, p.id))}
          data-testid={`preset-tab-${p.id}`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

// ── Display-options popover ────────────────────────────────────────────────

function DisplayOptionsButton({
  hiddenColumns,
  toggleColumn,
}: {
  hiddenColumns: Set<string>;
  toggleColumn: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Display options"
          title="Display options"
          data-testid="display-options-button"
        >
          <SlidersHorizontal />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} className={css.displayPopover}>
        <div className={css.displayMenu}>
          <p className={css.displayMenuLabel}>Columns</p>
          {WORKFLOW_COLUMNS.filter((col) => !col.alwaysOn).map((col) => {
            const visible = !hiddenColumns.has(col.id);
            return (
              <button
                key={col.id}
                type="button"
                role="menuitemcheckbox"
                aria-checked={visible}
                className={`${css.displayItem} ${visible ? css.displayItemOn : ''}`}
                onClick={() => toggleColumn(col.id)}
                data-testid={`display-option-${col.id}`}
              >
                <span className={css.displayItemCheck} aria-hidden>
                  {visible ? <Check size={14} strokeWidth={1.75} /> : null}
                </span>
                <span className={css.displayItemLabel}>{col.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}


function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function getRunLabel(run: WorkflowRun): string {
  if (run.description) {
    const firstLine = run.description.split('\n').map((line) => line.trim()).find(Boolean);
    if (firstLine) {
      return firstLine
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
        .replace(/^\s{0,3}#{1,6}\s+/, '')
        .replace(/^\s*[-*+]\s+/, '')
        .replace(/^\s*\d+\.\s+/, '')
        .replace(/(\*\*|__|\*|_|`|~~)/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    }
  }
  return `Run ${run.id.slice(0, 8)}`;
}

function normalizeTagValue(tag: string): string | null {
  const normalized = tag.trim().toLowerCase();
  return normalized || null;
}

function normalizeTagList(tags: string[]): string[] {
  const normalizedTags: string[] = [];
  const seen = new Set<string>();
  for (const tag of tags) {
    const normalized = normalizeTagValue(tag);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    normalizedTags.push(normalized);
  }
  return normalizedTags;
}

function getTagSaveErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return `Failed to save tags: ${error.message}`;
  }
  return 'Failed to save tags. Please try again.';
}

interface TagsCellProps {
  workflowRun: WorkflowRun;
  availableTags: WorkflowRunTagCount[];
  onSaveTags: (workflowRunId: string, tags: string[]) => Promise<unknown>;
}

export function TagsCell({ workflowRun, availableTags, onSaveTags }: TagsCellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [draftTags, setDraftTags] = useState<string[]>(workflowRun.tags ?? []);
  const [searchValue, setSearchValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const initializeEditor = useCallback(() => {
    setDraftTags(workflowRun.tags ?? []);
    setSearchValue('');
    setSaveError(null);
  }, [workflowRun.tags]);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (nextOpen) initializeEditor();
    else { setSearchValue(''); setSaveError(null); }
    setIsOpen(nextOpen);
  }, [initializeEditor]);

  const visibleTags = (workflowRun.tags ?? []).slice(0, 3);
  const extraTagCount = Math.max(0, (workflowRun.tags ?? []).length - visibleTags.length);
  const filteredSuggestions = useMemo(() => {
    const lowerSearch = searchValue.trim().toLowerCase();
    return availableTags.filter(({ tag }) => {
      if (draftTags.includes(tag)) return false;
      if (!lowerSearch) return true;
      return tag.includes(lowerSearch);
    });
  }, [availableTags, draftTags, searchValue]);

  const addTag = useCallback((rawTag: string) => {
    const normalized = normalizeTagValue(rawTag);
    if (!normalized) return;
    setSaveError(null);
    setDraftTags((prev) => normalizeTagList([...prev, normalized]));
    setSearchValue('');
  }, []);

  const removeTag = useCallback((tagToRemove: string) => {
    setSaveError(null);
    setDraftTags((prev) => prev.filter((tag) => tag !== tagToRemove));
  }, []);

  const hasChanges = JSON.stringify(draftTags) !== JSON.stringify(workflowRun.tags ?? []);
  const normalizedSearchValue = normalizeTagValue(searchValue);
  const showCreateOption = !!normalizedSearchValue
    && !draftTags.includes(normalizedSearchValue)
    && !availableTags.some(({ tag }) => tag === normalizedSearchValue);

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={css.tagsTrigger}
          aria-label={`Edit tags for ${getRunLabel(workflowRun)}`}
          onClick={(event) => event.stopPropagation()}
        >
          {visibleTags.length > 0 ? (
            <div className={css.tagsPreview}>
              {visibleTags.map((tag) => (
                <span key={tag} className={css.tagChip}>{tag}</span>
              ))}
              {extraTagCount > 0 && <span className={css.tagOverflow}>+{extraTagCount}</span>}
            </div>
          ) : (
            <span className={css.addTagsText}>Add tags</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={css.tagsPopover}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={css.tagsEditor}>
          <div className={css.editorSelectedTags}>
            {draftTags.length > 0 ? (
              draftTags.map((tag) => (
                <span key={tag} className={css.editorTagChip}>
                  {tag}
                  <button type="button" className={css.removeTagButton} onClick={() => removeTag(tag)} aria-label={`Remove ${tag}`}>
                    <X size={14} strokeWidth={1.75} />
                  </button>
                </span>
              ))
            ) : (
              <span className={css.noTagsText}>No tags selected</span>
            )}
          </div>
          <div className={css.tagsInputRow}>
            <Input
              value={searchValue}
              placeholder="Add or search tags"
              onChange={(event) => { setSaveError(null); setSearchValue(event.target.value); }}
              onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addTag(searchValue); } }}
            />
            <Button variant="outline" size="sm" onClick={() => addTag(searchValue)} disabled={!normalizedSearchValue}>Add</Button>
          </div>
          <div className={css.tagSuggestions}>
            {showCreateOption && (
              <button type="button" className={css.tagSuggestion} onClick={() => addTag(searchValue)}>
                <span>Create &ldquo;{normalizedSearchValue}&rdquo;</span>
                <span className={css.tagSuggestionCount}>new</span>
              </button>
            )}
            {filteredSuggestions.slice(0, 8).map(({ tag, count }) => (
              <button type="button" key={tag} className={css.tagSuggestion} onClick={() => addTag(tag)}>
                <span>{tag}</span>
                <span className={css.tagSuggestionCount}>{count}</span>
              </button>
            ))}
            {!showCreateOption && filteredSuggestions.length === 0 && (
              <div className={css.noSuggestionsText}>No matching tags</div>
            )}
          </div>
          {saveError && <p className={css.tagsSaveError} role="alert">{saveError}</p>}
          <div className={css.tagsEditorActions}>
            <Button variant="ghost" size="sm" onClick={() => handleOpenChange(false)}>Cancel</Button>
            <Button size="sm" disabled={!hasChanges || isSaving} onClick={async () => {
              setIsSaving(true); setSaveError(null);
              try { await onSaveTags(workflowRun.id, draftTags); handleOpenChange(false); }
              catch (error) { setSaveError(getTagSaveErrorMessage(error)); }
              finally { setIsSaving(false); }
            }}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── Workflow group accordion row ────────────────────────────────────────────

interface WorkflowGroupRowProps {
  group: WorkflowGroup;
  isExpanded: boolean;
  onToggle: () => void;
  onSelectRun?: (run: WorkflowRun) => void;
  getWorkflowRunHref?: (run: WorkflowRun) => string;
  tagOptions: WorkflowRunTagCount[];
  onSaveTags: (workflowRunId: string, tags: string[]) => Promise<unknown>;
  visibleColumns: WorkflowColumnConfig[];
}

function WorkflowGroupRow({
  group,
  isExpanded,
  onToggle,
  onSelectRun,
  getWorkflowRunHref,
  tagOptions,
  onSaveTags,
  visibleColumns,
}: WorkflowGroupRowProps) {
  const [showAll, setShowAll] = useState(false);
  const visibleRuns = showAll ? group.runs : group.runs.slice(0, MAX_VISIBLE_RUNS);
  const hasMore = group.runs.length > MAX_VISIBLE_RUNS;
  const visibleSet = useMemo(
    () => new Set(visibleColumns.map((c) => c.id)),
    [visibleColumns],
  );

  return (
    <div className={css.groupContainer}>
      <button
        type="button"
        className={css.groupRow}
        onClick={onToggle}
        data-testid={`workflow-group-${group.workflowName}`}
      >
        <span className={css.groupChevron}>
          {isExpanded
            ? <ChevronDown size={14} strokeWidth={1.75} />
            : <ChevronRight size={14} strokeWidth={1.75} />}
        </span>
        <span className={css.groupName}>{group.workflowName.replace(/_/g, ' ')}</span>
        <span className={css.groupRunCount}>{group.runCount} {group.runCount === 1 ? 'run' : 'runs'}</span>
      </button>

      {isExpanded && (
        <div className={css.subTable}>
          {visibleRuns.map((run) => {
            const href = getWorkflowRunHref?.(run);
            return (
              <div
                key={run.id}
                className={css.subTableRow}
                onClick={() => onSelectRun?.(run)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') onSelectRun?.(run); }}
                data-testid={`workflow-run-${run.id.slice(0, 8)}`}
              >
                {visibleSet.has('name') ? (
                  <span className={css.subCellName}>
                    {href ? (
                      <a
                        href={href}
                        className={css.subCellNameLink}
                        onClick={(event) => {
                          event.stopPropagation();
                          handlePlainLinkClick(
                            event,
                            onSelectRun ? () => onSelectRun(run) : undefined,
                          );
                        }}
                      >
                        {getRunLabel(run)}
                      </a>
                    ) : (
                      getRunLabel(run)
                    )}
                  </span>
                ) : null}
                {visibleSet.has('status') ? (
                  <span className={css.subCellStatus}>
                    <StatusBadge
                      label={WORKFLOW_RUN_STATUS_LABELS[run.status] || run.status}
                      {...(WORKFLOW_RUN_STATUS_COLORS[run.status] || {})}
                    />
                  </span>
                ) : null}
                {visibleSet.has('artifacts') ? (
                  <span className={css.subCellArtifacts}>
                    {(() => {
                      const artifacts = (run.ops ?? []).filter((op) => op.artifact_id);
                      if (artifacts.length === 0) return <span className={css.cellMuted}>&mdash;</span>;
                      return (
                        <div className={css.artifactPills}>
                          {artifacts.slice(0, 4).map((op) => (
                            <span key={op.name} className={css.artifactPill}>
                              {ARTIFACT_TYPE_LABELS[op.step_type as ArtifactType] ?? op.step_type}
                            </span>
                          ))}
                          {artifacts.length > 4 && (
                            <span className={css.cellMuted}>+{artifacts.length - 4}</span>
                          )}
                        </div>
                      );
                    })()}
                  </span>
                ) : null}
                {visibleSet.has('tags') ? (
                  <span className={css.subCellTags} onClick={(e) => e.stopPropagation()}>
                    <TagsCell workflowRun={run} availableTags={tagOptions} onSaveTags={onSaveTags} />
                  </span>
                ) : null}
                {visibleSet.has('duration') ? (
                  <span className={css.subCellTime}>
                    {run.status === 'running' || run.status === 'waiting_for_gpu' ? (
                      formatDuration(run.created_at)
                    ) : (
                      <span className={css.cellMuted}>&mdash;</span>
                    )}
                  </span>
                ) : null}
                {visibleSet.has('created') ? (
                  <span className={css.subCellTime}>
                    {relativeTime(run.created_at)}
                  </span>
                ) : null}
              </div>
            );
          })}
          {hasMore && !showAll && (
            <button
              type="button"
              className={css.showAllButton}
              onClick={(e) => { e.stopPropagation(); setShowAll(true); }}
            >
              Show all {group.runCount} runs
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main WorkflowsList ──────────────────────────────────────────────────────

interface WorkflowsListProps {
  onSelectWorkflowRun?: (workflowRun: WorkflowRun) => void;
  getWorkflowRunHref?: (workflowRun: WorkflowRun) => string;
  /** Controlled filter state. Falls back to internal state when omitted. */
  filterState?: FilterState;
  onFilterStateChange?: (next: FilterState) => void;
  /**
   * Extra toolbar icon buttons rendered at the start of the trailing
   * actions cluster (before the display/share/refresh icons). Used by the
   * webapp page to inject a Views button without bloating this component
   * with URL-aware concerns.
   */
  extraToolbarActions?: React.ReactNode;
}

export function WorkflowsList({
  onSelectWorkflowRun,
  getWorkflowRunHref,
  filterState: controlledFilterState,
  onFilterStateChange,
  extraToolbarActions,
}: WorkflowsListProps) {
  const [internalFilterState, setInternalFilterState] = useState<FilterState>(EMPTY_FILTER_STATE);
  const filterState = controlledFilterState ?? internalFilterState;
  const lastTrimRequestRef = useRef<string | null>(null);
  const setFilterState = useCallback(
    (next: FilterState) => {
      (onFilterStateChange ?? setInternalFilterState)(next);
    },
    [onFilterStateChange],
  );
  const [shareCopied, setShareCopied] = useState(false);
  const { collapsedGroups, toggleGroup, hiddenColumns, toggleColumn } = useWorkflowsListStore();
  const visibleColumns = useMemo(
    () => visibleColumnsFor(hiddenColumns),
    [hiddenColumns],
  );
  const gridTemplate = useMemo(
    () => gridTemplateFor(visibleColumns),
    [visibleColumns],
  );
  const compactGridTemplate = useMemo(
    () => gridTemplateFor(visibleColumns.filter((c) => !COMPACT_OMITTED_COLUMNS.has(c.id))),
    [visibleColumns],
  );
  const updateWorkflowRun = useUpdateWorkflowRun();

  const { data: workflows, isLoading, error, refetch } = useWorkflowRuns();
  const { data: workflowRunTags = [], isLoading: isTagsLoading } = useWorkflowRunTags();
  const isFiltered = filterState.filters.some((f) => f.values.length > 0);
  const handleSaveTags = useCallback((workflowRunId: string, tags: string[]) => {
    return updateWorkflowRun.mutateAsync({ id: workflowRunId, tags: normalizeTagList(tags) });
  }, [updateWorkflowRun]);

  const workflowNameUniverse = useMemo(() => {
    if (!workflows) {
      return [] as string[];
    }
    const counts = new Map<string, number>();
    for (const run of workflows) {
      const name = run.workflow_name || run.name || 'Ungrouped';
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([name]) => name);
  }, [workflows]);

  // Derive the universe of artifact types + dataset IDs actually used by any
  // run we've fetched. Keeping it scoped to the fetched runs means the filter
  // menu doesn't show zero-count noise from unused types/datasets.

  const artifactTypeUniverse = useMemo(() => {
    if (!workflows) return [] as string[];
    const seen = new Set<string>();
    for (const run of workflows) {
      for (const op of run.ops ?? []) {
        if (op.artifact_id && op.step_type) {
          seen.add(op.step_type);
        }
      }
    }
    return Array.from(seen).sort();
  }, [workflows]);

  const datasetIdUniverse = useMemo(() => {
    // dataset_ids is resolved server-side via
    // ml_artifacts.snapshot_id → dataset_snapshots.dataset_id, since prod
    // workflows never persist dataset_id directly on op.config.
    if (!workflows) return [] as string[];
    const seen = new Set<string>();
    for (const run of workflows) {
      for (const id of run.dataset_ids ?? []) {
        if (id) seen.add(id);
      }
    }
    return Array.from(seen);
  }, [workflows]);

  // Resolve dataset IDs to human-readable names. The `useDatasets` query
  // returns every dataset the user can see; we only consult it for IDs that
  // appear in our universe. Missing entries fall back to a short id.
  const { data: datasetsResult } = useDatasets();
  const datasetsById = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of datasetsResult?.data ?? []) {
      map.set(d.id, d.name);
    }
    return map;
  }, [datasetsResult?.data]);

  const getWorkflowFilterValue = useCallback(
    (run: WorkflowRun, attributeId: string): string | string[] | null | undefined => {
      if (attributeId === 'status') {
        return run.status;
      }
      if (attributeId === 'workflow_name') {
        return run.workflow_name || run.name || 'Ungrouped';
      }
      if (attributeId === 'tag') {
        return run.tags ?? [];
      }
      if (attributeId === 'artifact_type') {
        const out: string[] = [];
        for (const op of run.ops ?? []) {
          if (op.artifact_id && op.step_type) {
            out.push(op.step_type);
          }
        }
        return out;
      }
      if (attributeId === 'dataset') {
        return run.dataset_ids ?? [];
      }
      return null;
    },
    [],
  );

  const availableFilterIds = useMemo(() => {
    const ids = new Set<WorkflowFilterId>(['status', 'workflow_name', 'created']);
    if (workflowRunTags.length > 0) {
      ids.add('tag');
    }
    if (artifactTypeUniverse.length > 0) {
      ids.add('artifact_type');
    }
    if (datasetIdUniverse.length > 0) {
      ids.add('dataset');
    }
    return ids;
  }, [workflowRunTags, artifactTypeUniverse, datasetIdUniverse]);

  const filterConfigs = useMemo((): FilterConfig[] => {
    const runsForCounts = workflows ?? [];
    const statusCounts = computeOptionCounts(
      runsForCounts,
      filterState,
      'status',
      getWorkflowFilterValue,
    );
    const tagCounts = computeOptionCounts(
      runsForCounts,
      filterState,
      'tag',
      getWorkflowFilterValue,
    );
    const nameCounts = computeOptionCounts(
      runsForCounts,
      filterState,
      'workflow_name',
      getWorkflowFilterValue,
    );
    const artifactTypeCounts = computeOptionCounts(
      runsForCounts,
      filterState,
      'artifact_type',
      getWorkflowFilterValue,
    );
    const datasetCounts = computeOptionCounts(
      runsForCounts,
      filterState,
      'dataset',
      getWorkflowFilterValue,
    );

    const configs: FilterConfig[] = [
      {
        kind: 'multi-select',
        id: 'status',
        label: 'Status',
        options: WORKFLOW_STATUS_FILTER_ORDER.map((s) => ({
          value: s,
          label: WORKFLOW_RUN_STATUS_LABELS[s],
          count: statusCounts.get(s) ?? 0,
        })),
      },
    ];
    if (workflowRunTags.length > 0) {
      configs.push({
        kind: 'multi-select',
        id: 'tag',
        label: 'Tag',
        options: workflowRunTags.map(({ tag }) => ({
          value: tag,
          label: tag,
          count: tagCounts.get(tag) ?? 0,
        })),
      });
    }
    configs.push({
      kind: 'multi-select',
      id: 'workflow_name',
      label: 'Workflow',
      options: workflowNameUniverse.map((name) => ({
        value: name,
        label: name.replace(/_/g, ' '),
        count: nameCounts.get(name) ?? 0,
      })),
    });
    if (artifactTypeUniverse.length > 0) {
      configs.push({
        kind: 'multi-select',
        id: 'artifact_type',
        label: 'Artifact',
        options: artifactTypeUniverse.map((type) => ({
          value: type,
          label: ARTIFACT_TYPE_LABELS[type as ArtifactType] ?? type,
          count: artifactTypeCounts.get(type) ?? 0,
        })),
      });
    }
    if (datasetIdUniverse.length > 0) {
      configs.push({
        kind: 'multi-select',
        id: 'dataset',
        label: 'Dataset',
        options: datasetIdUniverse
          .map((id) => ({
            value: id,
            label: datasetsById.get(id) ?? id.slice(0, 8),
            count: datasetCounts.get(id) ?? 0,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      });
    }
    configs.push(createdTimeRangeFilterConfig(Clock));
    return configs;
  }, [
    workflows,
    filterState,
    getWorkflowFilterValue,
    workflowRunTags,
    workflowNameUniverse,
    artifactTypeUniverse,
    datasetIdUniverse,
    datasetsById,
  ]);

  const filteredRuns = useMemo(() => {
    if (!workflows) {
      return [];
    }
    const createdRange = resolveCreatedRange(filterState);
    return applyFilters(workflows, filterState, getWorkflowFilterValue, filterConfigs).filter((run) =>
      matchesCreatedRange(run.created_at, createdRange),
    );
  }, [workflows, filterState, getWorkflowFilterValue, filterConfigs]);

  useEffect(() => {
    if (isLoading || isTagsLoading || !workflows) {
      return;
    }
    const trimmed = filterState.filters.filter((f) =>
      availableFilterIds.has(f.attributeId as WorkflowFilterId),
    );
    const currentSignature = filterSignature(filterState.filters);
    const trimmedSignature = filterSignature(trimmed);
    if (currentSignature === trimmedSignature) {
      lastTrimRequestRef.current = null;
      return;
    }
    const trimRequest = `${currentSignature}->${trimmedSignature}`;
    if (lastTrimRequestRef.current !== trimRequest) {
      lastTrimRequestRef.current = trimRequest;
      setFilterState({ filters: trimmed });
    }
  }, [availableFilterIds, filterState, isLoading, isTagsLoading, setFilterState, workflows]);

  const groups = useMemo(() => groupRunsByWorkflow(filteredRuns), [filteredRuns]);

  if (error) {
    return (
      <CenteredContainer>
        <p className={css.errorTitle}>Failed to load workflows</p>
        <p className={css.errorSub}>{(error as Error).message}</p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className={css.errorRetry}>
          <RefreshCw /> Retry
        </Button>
      </CenteredContainer>
    );
  }

  const hasData = groups.length > 0;

  return (
    <div className={css.root} data-testid="workflows-list">
      <div className={css.toolbar}>
        <ListViewToolbar
          filterConfigs={filterConfigs}
          value={filterState}
          onChange={setFilterState}
          leading={
            <PresetTabs
              filterState={filterState}
              onFilterStateChange={setFilterState}
            />
          }
          trailingActions={
            <>
              {extraToolbarActions}
              <DisplayOptionsButton
                hiddenColumns={hiddenColumns}
                toggleColumn={toggleColumn}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={shareCopied ? 'Link copied' : 'Copy share link'}
                title={shareCopied ? 'Link copied!' : 'Copy share link'}
                data-testid="share-link-button"
                onClick={() => {
                  if (typeof window === 'undefined' || !navigator.clipboard) {
                    return;
                  }
                  void navigator.clipboard
                    .writeText(window.location.href)
                    .then(() => {
                      setShareCopied(true);
                      window.setTimeout(() => setShareCopied(false), 1500);
                    })
                    .catch(() => {
                      // Clipboard unavailable; swallow silently for now.
                    });
                }}
              >
                {shareCopied ? <Check /> : <LinkIcon />}
              </Button>
              {/*
                No manual refresh button: `useWorkflowRuns` already polls
                every 5s when active runs exist (15s otherwise) via React
                Query, and refetches on window focus. New data lands in the
                same component instances so scroll position and the
                expanded-group set survive untouched.
              */}
            </>
          }
        />
      </div>

      {isLoading ? (
        <CenteredContainer>
          <div className={css.loadingDots}>Loading workflows...</div>
        </CenteredContainer>
      ) : !hasData ? (
        <CenteredContainer>
          <div className={css.emptyState}>
            <p className={css.emptyTitle}>
              {isFiltered ? 'No workflows match this filter' : 'No workflows yet'}
            </p>
            <p className={css.emptySub}>
              {isFiltered
                ? 'Try adjusting filters or clear them to see all workflows.'
                : 'Create your first workflow to start training models.'}
            </p>
            <div className={css.emptyActions}>
              {isFiltered && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilterState(EMPTY_FILTER_STATE)}
                >
                  Show all
                </Button>
              )}
            </div>
          </div>
        </CenteredContainer>
      ) : (
        <div
          className={css.accordionWrapper}
          // The header and the run rows read their columns from these
          // properties. A narrow table on touch or a phone uses the compact set.
          style={
            {
              '--wf-cols': gridTemplate,
              '--wf-cols-compact': compactGridTemplate,
            } as React.CSSProperties
          }
        >
          <div
            className={css.tableHeader}
            role="row"
            data-testid="workflows-table-header"
          >
            {visibleColumns.map((col) => (
              <span key={col.id} role="columnheader" className={HEADER_CELL_CLASS[col.id]}>
                {col.label}
              </span>
            ))}
          </div>
          {groups.map((group) => (
            <WorkflowGroupRow
              key={group.workflowName}
              group={group}
              isExpanded={!collapsedGroups.has(group.workflowName)}
              onToggle={() => toggleGroup(group.workflowName)}
              onSelectRun={onSelectWorkflowRun}
              getWorkflowRunHref={getWorkflowRunHref}
              tagOptions={workflowRunTags}
              onSaveTags={handleSaveTags}
              visibleColumns={visibleColumns}
            />
          ))}
        </div>
      )}
    </div>
  );
}
